<div class="approvers-selector-dialog">
    <!-- Header -->
    <div class="dialog-header">
        <div class="header-content">
            <mat-icon class="header-icon">approval</mat-icon>
            <div class="header-text">
                <h2>Configure Approval Workflow</h2>
                <p class="subtitle">Define who must approve this policy before publication</p>
            </div>
        </div>
        <button mat-icon-button (click)="cancel()" class="close-btn">
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <!-- Content -->
    <div class="dialog-content">
        <!-- Templates Section -->
        <div class="templates-section">
            <span class="section-label">Quick Templates:</span>
            <div class="template-buttons">
                <button mat-stroked-button (click)="applyTemplate('simple')" [disabled]="data.mode === 'view'">
                    <mat-icon>person</mat-icon>
                    Simple (1 Approver)
                </button>
                <button mat-stroked-button (click)="applyTemplate('standard')" [disabled]="data.mode === 'view'">
                    <mat-icon>people</mat-icon>
                    Standard (2 Steps)
                </button>
                <button mat-stroked-button (click)="applyTemplate('executive')" [disabled]="data.mode === 'view'">
                    <mat-icon>groups</mat-icon>
                    Executive (3 Steps)
                </button>
                <button mat-stroked-button (click)="clearAllSteps()" class="clear-btn" 
                        *ngIf="approvalSteps.length > 0" [disabled]="data.mode === 'view'">
                    <mat-icon>clear_all</mat-icon>
                    Clear All
                </button>
            </div>
        </div>

        <!-- Approval Steps List -->
        <div class="steps-container" 
             cdkDropList 
             (cdkDropListDropped)="dropStep($event)"
             [cdkDropListDisabled]="data.mode === 'view'">
            
            <h3 class="steps-title" *ngIf="approvalSteps.length > 0">
                <mat-icon>checklist</mat-icon>
                Approval Steps ({{ approvalSteps.length }})
            </h3>

            <!-- Step Cards -->
            <div class="step-card" 
                 *ngFor="let step of approvalSteps; let i = index"
                 cdkDrag
                 [class.incomplete]="isStepIncomplete(step)">
                
                <!-- Drag Handle -->
                <div class="drag-handle" cdkDragHandle *ngIf="data.mode === 'edit'">
                    <mat-icon>drag_indicator</mat-icon>
                </div>

                <!-- Step Number -->
                <div class="step-number">
                    <span class="number">{{ step.order + 1 }}</span>
                </div>

                <!-- Step Content -->
                <div class="step-content">
                    <div class="step-header">
                        <!-- Editable Step Name -->
                        <input *ngIf="data.mode === 'edit'"
                               #stepNameInput
                               class="step-name-input"
                               [(ngModel)]="step.name"
                               placeholder="Step name..."
                               (blur)="onStepNameBlur(step)"
                               (keydown.enter)="stepNameInput.blur()">
                        <span class="step-name" *ngIf="data.mode === 'view'">{{ step.name }}</span>
                        
                        <mat-chip class="status-chip" [color]="getStepStatusColor(step.status)">
                            <mat-icon>{{ getStepStatusIcon(step.status) }}</mat-icon>
                            {{ step.status | titlecase }}
                        </mat-chip>
                    </div>
                    
                    <!-- Approver Selection - Always show dropdown in edit mode -->
                    <mat-form-field *ngIf="data.mode === 'edit'" appearance="outline" class="approver-select">
                        <mat-label>Select Approver</mat-label>
                        <mat-select [value]="getSelectedUserOption(step)" 
                                    (selectionChange)="updateStepApprover(step, $event.value)"
                                    panelClass="approver-select-panel">
                            <mat-select-trigger>
                                <div class="selected-approver-display" *ngIf="!isStepIncomplete(step)">
                                    <mat-icon>{{ step.approver.type === 'hat' ? 'badge' : 'person' }}</mat-icon>
                                    <span>{{ step.approver.name }}</span>
                                    <span class="via-hat" *ngIf="step.approver.type === 'hat'">(via hat)</span>
                                </div>
                                <span *ngIf="isStepIncomplete(step)" class="placeholder-text">Select approver...</span>
                            </mat-select-trigger>
                            
                            <!-- Search Input -->
                            <div class="select-search-box">
                                <mat-icon>search</mat-icon>
                                <input matInput 
                                       [(ngModel)]="approverSearchTerm"
                                       (ngModelChange)="filterApprovers()"
                                       placeholder="Search staff members or hats..."
                                       (click)="$event.stopPropagation()">
                                <button mat-icon-button *ngIf="approverSearchTerm" (click)="clearApproverSearch(); $event.stopPropagation()">
                                    <mat-icon>close</mat-icon>
                                </button>
                            </div>
                            
                            <mat-option *ngFor="let user of filteredApprovers" [value]="user">
                                <div class="user-option">
                                    <mat-icon>{{ user.type === 'hat' ? 'badge' : 'person' }}</mat-icon>
                                    <span class="user-name">{{ user.name }}</span>
                                    <span class="user-hat" *ngIf="user.hatName">({{ user.hatName }})</span>
                                </div>
                            </mat-option>
                            
                            <mat-option disabled *ngIf="filteredApprovers.length === 0">
                                <span class="no-results-text">No staff found</span>
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <!-- View Mode - Just show approver info -->
                    <div class="approver-info" *ngIf="data.mode === 'view' && !isStepIncomplete(step)">
                        <mat-icon>{{ step.approver.type === 'hat' ? 'badge' : 'person' }}</mat-icon>
                        <span class="approver-name">{{ step.approver.name }}</span>
                        <span class="approver-via" *ngIf="step.approver.type === 'hat'">
                            (via hat)
                        </span>
                    </div>

                    <!-- Warning for incomplete steps -->
                    <div class="incomplete-warning" *ngIf="isStepIncomplete(step)">
                        <mat-icon color="warn">warning</mat-icon>
                        <span>Select an approver for this step</span>
                    </div>
                </div>

                <!-- Remove Button -->
                <button mat-icon-button color="warn" (click)="removeStep(i)" 
                        *ngIf="data.mode === 'edit'"
                        matTooltip="Remove step">
                    <mat-icon>delete</mat-icon>
                </button>

                <!-- Connector Line -->
                <div class="connector-line" *ngIf="i < approvalSteps.length - 1">
                    <mat-icon>arrow_downward</mat-icon>
                </div>
            </div>

            <!-- Empty State -->
            <div class="empty-state" *ngIf="approvalSteps.length === 0">
                <mat-icon>approval</mat-icon>
                <h3>No Approval Steps</h3>
                <p>This policy will not require approval.</p>
                <p class="hint">Use a template above or add custom steps below.</p>
            </div>
        </div>

        <!-- Add Step Section -->
        <div class="add-step-section" *ngIf="data.mode === 'edit'">
            <button mat-stroked-button color="primary" (click)="addNewStep()">
                <mat-icon>add</mat-icon>
                Add Approval Step
            </button>
        </div>
    </div>

    <!-- Summary Footer -->
    <div class="dialog-summary">
        <div class="summary-info">
            <mat-icon [color]="approvalSteps.length > 0 ? 'primary' : ''">
                {{ approvalSteps.length > 0 ? 'approval' : 'public' }}
            </mat-icon>
            <span *ngIf="approvalSteps.length === 0">
                No approval required - policy can be published directly
            </span>
            <span *ngIf="approvalSteps.length > 0">
                {{ approvalSteps.length }} approval step{{ approvalSteps.length > 1 ? 's' : '' }} required
            </span>
        </div>
        <div class="warning" *ngIf="hasIncompleteSteps">
            <mat-icon color="warn">warning</mat-icon>
            <span>Some steps need approvers selected</span>
        </div>
    </div>

    <!-- Actions -->
    <div class="dialog-actions">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-flat-button color="primary" 
                (click)="confirm()" 
                [disabled]="!isValid || data.mode === 'view'">
            <mat-icon>check</mat-icon>
            Confirm Workflow
        </button>
    </div>
</div>
